import { useCallback, useEffect, useRef } from "react"
import Notification from "@/components/Notification"
import { useChatGlobalStateStore } from "@/store/ChatGlobalState"
import { useGlobalStateStore } from "@/store/GlobalState"
import useStartChat from "@/hooks/useStartChat"
import ApiService from "@/services/Api"
import ErrorHandlerService from "@/services/ErrorHandler"
import { ErrorType } from "@/hooks/useValidation"
import { Chat } from "@/protocols/chatGlobalStateProtocol"
import { Client, ClientCatalog, Contact } from "@/protocols/clientCatalog"
import { getPhoneContact, getPhoneNumber, getShareLinkPhoneNumber } from "@/utils/contact"
import { isSamePhone, keepOnlyDigits } from "@/utils/phone"
import { runAfterReactRender } from "@/utils/node"
import { INBOX_OPEN_WABA_TEMPLATE_SELECTOR_EVENT } from "@/pages/Attendance/boardNavigationConstants"

type OpenChatParams = {
	phone?: string | null
	clientId?: number | string | null
}

const useOpenChatByPhone = () => {
	const chatGlobalStateStore = useChatGlobalStateStore()
	const globalStateStore = useGlobalStateStore()
	const { startChat } = useStartChat()

	const latestStoresRef = useRef({ chatGlobalStateStore, globalStateStore, startChat })

	useEffect(() => {
		latestStoresRef.current = { chatGlobalStateStore, globalStateStore, startChat }
	})

	const requestWABATemplateSelectorIfNeeded = useCallback((chat: Chat | undefined) => {
		const { globalStateStore: latestGlobalStateStore, chatGlobalStateStore: latestChatStore } = latestStoresRef.current

		const isWABA = (chat?.channelType ?? latestGlobalStateStore.currentChannel?.channelType) === "waba"

		if (!isWABA) {
			return
		}

		const latestChat = chat ? latestChatStore.chat.getById(chat.id) ?? chat : latestChatStore.chat.current
		const hasActiveCommunicationWindow = latestChat?.wabaChannelChatCommunicationPermission?.status === "active"

		if (hasActiveCommunicationWindow) {
			return
		}

		runAfterReactRender(() => {
			window.dispatchEvent(new CustomEvent(INBOX_OPEN_WABA_TEMPLATE_SELECTOR_EVENT))
		})
	}, [])

	const startChatForClient = useCallback(async (client: Client): Promise<void> => {
		const phoneContact = getPhoneContact(client) as Contact | undefined

		if (!phoneContact) {
			Notification.error({ message: "Número de WhatsApp inválido." })
			return
		}

		const errorMessage = await latestStoresRef.current.startChat(client.id, phoneContact.id)

		if (errorMessage) {
			Notification.error({ message: errorMessage })
			return
		}

		requestWABATemplateSelectorIfNeeded(latestStoresRef.current.chatGlobalStateStore.chat.current)
	}, [requestWABATemplateSelectorIfNeeded])

	const startChatByClientIdFromBackend = useCallback(async (clientId: number): Promise<void> => {
		try {
			const { data } = await ApiService.get(`/clients/${clientId}`)

			await startChatForClient(data.client as Client)
		} catch (err) {
			ErrorHandlerService.handle(err as ErrorType)
		}
	}, [startChatForClient])

	const startChatByPhoneFromBackend = useCallback(async (phone: string): Promise<void> => {
		try {
			const { data } = await ApiService.get("/clients/count-or-search", { params: { search: keepOnlyDigits(phone) } })
			const client = (data as ClientCatalog).clients?.find(searchedClient => isSamePhone(getPhoneNumber(searchedClient), phone))

			if (!client) {
				Notification.warning({ message: "Contato não encontrado para abrir o chat." })
				return
			}

			await startChatForClient(client)
		} catch (err) {
			ErrorHandlerService.handle(err as ErrorType)
		}
	}, [startChatForClient])

	const openChat = useCallback(async ({ phone, clientId }: OpenChatParams) => {
		const clientIdAsNumber = clientId ? Number(clientId) : undefined
		const hasValidClientId = Number.isInteger(clientIdAsNumber)
		const phoneDigits = keepOnlyDigits(phone || "")

		if (!phoneDigits && !hasValidClientId) {
			return
		}

		if (phoneDigits) {
			await latestStoresRef.current.chatGlobalStateStore.chatListPanel.chatListFilter.update({ text: phoneDigits }, { skipReactiveReload: true })
		}

		await latestStoresRef.current.chatGlobalStateStore.chat.loadAllFromServer({ isolated: true })

		const { chatGlobalStateStore: latestChatStore } = latestStoresRef.current

		const searchedChat = latestChatStore.chat.list.find(chat => {
			if (hasValidClientId && (chat?.client as Client)?.id === clientIdAsNumber) {
				return true
			}

			return Boolean(phoneDigits) && isSamePhone(getShareLinkPhoneNumber(chat?.client as Client, chat.type), phoneDigits)
		})

		if (!searchedChat) {
			if (hasValidClientId) {
				await startChatByClientIdFromBackend(clientIdAsNumber as number)
			} else {
				await startChatByPhoneFromBackend(phoneDigits)
			}

			return
		}

		await latestChatStore.chat.openById(searchedChat.id)

		requestWABATemplateSelectorIfNeeded(searchedChat)
	}, [startChatByClientIdFromBackend, startChatByPhoneFromBackend, requestWABATemplateSelectorIfNeeded])

	const openChatByPhone = useCallback((phone: string, clientId?: number | string | null) => (
		openChat({ phone, clientId })
	), [openChat])

	return { openChatByPhone, openChat, startChatByClientIdFromBackend }
}

export default useOpenChatByPhone
